import { FigureReorderDialog } from "components/system";
import { useConfirm } from "material-ui-confirm";
import { useModal } from "mui-modal-provider";
import { useEffect, useState } from "react";
import { useAppContext } from "../../../../../context/AuthContext";
import { ArticleController, ArticleControllerType } from "../../../../../controller/article/ArticleController";
import Labels from "../../../../../lang/Labels_en";
import MessageType from "../../../../../utils/MessageType";
import {
  getTypeFromNameFile,
  handleErrorMessage,
  handleFileType,
  parseArticleForBackend,
  useEffectOnce,
} from "../../../../../utils/Utils";
import EnumDesignation from "../../../../../utils/enum/EnumDesignation";

interface PaperFilesServiceType {
  uploadFile: any;
  articleSave: any;
  designationOptions: any;
  openDialog: any;
  handleOpenDialog: any;
  handleCloseDialog: any;
  handleResetDialog: any;
  disableDesignation: any;
  setDisableDesignation: any;
  files: any;
  setFiles: any;
  uploading: boolean;
  deleteFile: (fileId: number) => void;
  filesByDesignation: any;
  selectedDesigantion: any;
  deleteFileGroup: (code: string) => void;
}

const designationOptions: any = [
  { name: Labels.MANUSCRIPT, code: EnumDesignation.MANUSCRIPT },
  { name: Labels.FIGURE, code: EnumDesignation.FIGURE },
  { name: "Supplementary figure", code: EnumDesignation.SUPPLEMENTARY_FIGURE },
  { name: Labels.ADDITIONAL_FILE, code: EnumDesignation.ADDITIONAL_FILE },
  { name: Labels.VIDEO_FILE, code: EnumDesignation.VIDEO_FILE },
  { name: "Tables (combine tables into one document)", code: EnumDesignation.TABLES },
];

export default function PaperFilesService({ article, setArticle, handleNext }: any): PaperFilesServiceType {
  const { axiosUploadFile, axiosArticleUpdate, axiosPostDeleteFile, axiosPostDeleteFiles }: ArticleControllerType =
    ArticleController();

  const { showMessage } = useAppContext();
  const { showModal } = useModal();
  const confirm = useConfirm();

  const [filesList, setFilesList] = useState<any>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [disableDesignation, setDisableDesignation] = useState<boolean>(false);
  const [replaceFile, setReplaceFile] = useState<any>();

  const [files, setFiles] = useState<File[]>([]);
  const [selectedDesigantion, setSelectedDesignation] = useState<any>();

  const [filesByDesignation, setFilesByDesignation] = useState<File[]>([]);

  const updateFiles = (fileList: any) => {
    const arrayFile: any = [];
    // manuscript
    const fileManuscript = fileList?.find((f: any) => f.type === EnumDesignation.MANUSCRIPT);
    arrayFile.push({
      id: fileManuscript ? fileManuscript.id : null,
      name: fileManuscript?.filename ?? "required*",
      designation: Labels.MANUSCRIPT,
      type: handleFileType(EnumDesignation.MANUSCRIPT)?.join(", ").replaceAll(".", ""),
      location: fileManuscript?.location ?? null,
      showAdd: !Boolean(fileManuscript),
      showDelete: Boolean(fileManuscript),
      showReplace: Boolean(fileManuscript),
      code: EnumDesignation.MANUSCRIPT,
      color: fileManuscript ? "green" : "red",
      tooltip: "only one manuscript file allowed",
    });

    // figure
    const fileFigureArray = fileList?.filter((f: any) => f.type === EnumDesignation.FIGURE);
    arrayFile.push({
      name: fileFigureArray?.length > 0 ? fileFigureArray?.length : "no files",
      designation: Labels.FIGURE,
      files: fileFigureArray,
      type: handleFileType(EnumDesignation.FIGURE)?.join(", ").replaceAll(".", ""),
      showAdd: true,
      showDelete: fileFigureArray?.length > 0,
      code: EnumDesignation.FIGURE,
      color: fileFigureArray?.length > 0 ? "green" : "black",
      tooltip: "Please submit high quality figures",
    });

    // supplemenatry figure
    const fileSupplementaryFigureArray = fileList?.filter((f: any) => f.type === EnumDesignation.SUPPLEMENTARY_FIGURE);
    arrayFile.push({
      name: fileSupplementaryFigureArray?.length > 0 ? fileSupplementaryFigureArray?.length : "no files",
      designation: Labels.SUPPLEMENTARY_FIGURE,
      files: fileSupplementaryFigureArray,
      type: handleFileType(EnumDesignation.SUPPLEMENTARY_FIGURE)?.join(", ").replaceAll(".", ""),
      showAdd: true,
      showDelete: fileSupplementaryFigureArray?.length > 0,
      code: EnumDesignation.SUPPLEMENTARY_FIGURE,
      color: fileSupplementaryFigureArray?.length > 0 ? "green" : "black",
      tooltip: "Please submit high quality figures",
    });

    // additional info
    const fileAdditionalInfoArray = fileList?.filter((f: any) => f.type === EnumDesignation.ADDITIONAL_FILE);
    arrayFile.push({
      name: fileAdditionalInfoArray?.length > 0 ? fileAdditionalInfoArray?.length : "no files",
      designation: Labels.ADDITIONAL_FILE,
      files: fileAdditionalInfoArray,
      type: handleFileType(EnumDesignation.ADDITIONAL_FILE)?.join(", ").replaceAll(".", ""),
      showAdd: true,
      showDelete: fileAdditionalInfoArray?.length > 0,
      code: EnumDesignation.ADDITIONAL_FILE,
      color: fileAdditionalInfoArray?.length > 0 ? "green" : "black",
    });

    // video
    const fileVideoArray = fileList?.filter((f: any) => f.type === EnumDesignation.VIDEO_FILE);
    arrayFile.push({
      name: fileVideoArray?.length > 0 ? fileVideoArray?.length : "no files",
      designation: Labels.VIDEO_FILE,
      files: fileVideoArray,
      type: handleFileType(EnumDesignation.VIDEO_FILE)?.join(", ").replaceAll(".", ""),
      showAdd: true,
      showDelete: fileVideoArray?.length > 0,
      code: EnumDesignation.VIDEO_FILE,
      color: fileVideoArray?.length > 0 ? "green" : "black",
    });

    // tables
    const fileTables = fileList?.find((f: any) => f.type === EnumDesignation.TABLES);
    arrayFile.push({
      id: fileTables?.id ?? null,
      name: fileTables?.filename ?? "no files",
      designation: Labels.TABLES,
      type: handleFileType(EnumDesignation.TABLES)?.join(", ").replaceAll(".", ""),
      location: fileTables?.location ?? null,
      showAdd: !Boolean(fileTables),
      showDelete: Boolean(fileTables),
      showReplace: Boolean(fileTables),
      code: EnumDesignation.TABLES,
      color: fileTables ? "green" : "black",
      tooltip: "combine tables into one document",
    });

    setFilesByDesignation(arrayFile);
  };

  useEffectOnce(() => {
    setFilesList(article?.fileList);
  });

  useEffect(() => {
    if (filesList) {
      updateFiles(filesList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesList]);

  const deleteFileCall = async (fileId: any) => {
    if (+fileId) {
      await axiosPostDeleteFile(article.id, fileId)
        .then((response: any) => {
          setFilesList(filesList.filter((file: any) => file.id !== fileId));
        })
        .catch((error: any) => handleErrorMessage(showMessage, error));
    } else {
      setFilesList(filesList.filter((file: any) => file.id !== fileId));
    }
  };

  const deleteFile = (fileId: number) => {
    confirm({
      description: "Are you sure you want delete?",
      title: "Delete",
      confirmationText: Labels.YES,
      cancellationText: Labels.NO,
      confirmationButtonProps: {
        variant: "contained",
        color: "primary",
      },
    })
      .then(() => deleteFileCall(fileId))
      .catch(() => {});
  };

  const deleteFileGroup = (code: string) => {
    confirm({
      description: "Are you sure you want delete?",
      title: "Delete",
      confirmationText: Labels.YES,
      cancellationText: Labels.NO,
      confirmationButtonProps: {
        variant: "contained",
        color: "primary",
      },
    })
      .then(() => {
        const fileDelete = filesList?.filter((f: any) => f.type === code);

        const fileDeleteLocal = fileDelete?.filter((f: any) => isNaN(f.id))?.map(({ id }: any) => id);
        const fileDeleteDatabase = fileDelete?.filter((f: any) => !isNaN(f.id))?.map(({ id }: any) => id);

        let newFiles = filesList;

        if (fileDeleteLocal?.length > 0) {
          newFiles = newFiles?.filter((f: any) => !fileDeleteLocal.includes(f.id));
        }

        if (fileDeleteDatabase?.length > 0) {
          axiosPostDeleteFiles(article.id, fileDeleteDatabase)
            .then((response: any) => {
              setFilesList(
                filesList.filter((file: any) => ![...fileDeleteLocal, ...fileDeleteDatabase].includes(file.id))
              );
            })
            .catch((error: any) => handleErrorMessage(showMessage, error));
        } else {
          setFilesList(newFiles);
        }
      })
      .catch(() => {});
  };

  const uploadFile = async (file: any, values: any, setFieldValue: any) => {
    if (filesList.some((f: any) => f.filename === file[0].name.replaceAll(" ", ""))) {
      showMessage("File with that name already added", MessageType.ERROR);
    } else if (
      values.designation.code === EnumDesignation.TABLES &&
      filesList.some((f: any) => f.designation === EnumDesignation.TABLES)
    ) {
      showMessage("You can only add one file of type Tables", MessageType.ERROR);
    } else {
      if (replaceFile) {
        await deleteFileCall(replaceFile);
      }
      const filesNew = filesList?.filter((f: any) => f.id !== replaceFile);
      setUploading(true);
      axiosUploadFile(article.id, file)
        .then((response: any) => {
          setFilesList([
            ...filesNew,
            {
              id: `file-${filesNew.length}`,
              name: response.data.fileName,
              filename: response.data.fileName,
              type: values.designation.code,
              fileType: getTypeFromNameFile(file[0].name),
              location: URL.createObjectURL(file[0]),
            },
          ]);
          handleCloseDialog();
          handleResetDialog(false, setFieldValue, [], "", "");
          setUploading(false);
        })
        .catch((error: any) => {
          setUploading(false);
          handleErrorMessage(showMessage, error);
        });
    }
  };

  const articleUpdate = (newArticle: any) => {
    axiosArticleUpdate({
      ...newArticle,
      authorsIds: article.articleAuthors ? article.articleAuthors.map((a: any) => a.id) : undefined,
    })
      .then((response: any) => {
        //update state samo za fajlove
        setArticle({ ...article, fileList: response.data.files });
        handleNext();
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const articleSave = () => {
    const files = filesList.map((f: any) => ({
      id: f.id,
      filename: f.name ?? f.filename,
      type: f.type,
      fileType: f.fileType,
      revision: 0,
      figureTitle: f.figureTitle,
    }));
    const newArticle = {
      ...parseArticleForBackend(article),
      files: filesList.length > 0 ? files : [],
      fileList: filesList.length > 0 ? files : [],
    };

    if (!filesList?.find((f: any) => f.type === EnumDesignation.MANUSCRIPT)) {
      showMessage("Manuscript is required file.", MessageType.ERROR);
      return;
    }

    if (
      filesList?.length === 0 ||
      !filesList?.some((f: any) =>
        ["png", "jpg", "tiff"].includes(getTypeFromNameFile((f?.name ?? f?.filename)?.toLowerCase()))
      )
    ) {
      confirm({
        description: Labels.YOU_WANT_TO_MOVE_ON,
        title: Labels.PICTURE_IS_UPLOADED,
        confirmationText: Labels.YES,
        cancellationText: Labels.NO,
        confirmationButtonProps: {
          variant: "contained",
          color: "primary",
        },
      })
        .then(() => {
          reorderFiles(newArticle);
        })
        .catch(() => {});
    } else {
      reorderFiles(newArticle);
    }
  };

  const reorderFiles = (newArticle: any) => {
    const isExistFigures = newArticle.files.some((f: any) =>
      [EnumDesignation.FIGURE, EnumDesignation.SUPPLEMENTARY_FIGURE].includes(f.type)
    );
    if (isExistFigures) {
      showModal(FigureReorderDialog, { article: newArticle, onConfirm: articleUpdate });
    } else {
      
      const newFiles = newArticle.files?.map((f: any) => {
        const newF = f;
        delete newF.id;
        return newF;
      });

      newArticle.files = newFiles;
      articleUpdate(newArticle);
    }
  };

  const handleOpenDialog = (code: string, replaceId?: any) => {
    setOpenDialog(true);
    setReplaceFile(replaceId);
    setSelectedDesignation(designationOptions.find((d: any) => d.code === code));
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleResetDialog = (disabled: boolean, setFieldValue: any, files: File[], size: any, type: any) => {
    setDisableDesignation(disabled);
    if (files !== null) setFiles(files);
    setFieldValue("size", size);
    setFieldValue("type", type);
  };

  return {
    uploadFile,
    articleSave,
    designationOptions,
    openDialog,
    handleOpenDialog,
    handleCloseDialog,
    handleResetDialog,
    disableDesignation,
    setDisableDesignation,
    files,
    setFiles,
    uploading,
    deleteFile,
    filesByDesignation,
    selectedDesigantion,
    deleteFileGroup,
  };
}

export type { PaperFilesServiceType };

